import { type EnvironmentId, type ThreadId } from "@cmmd-forge/contracts";
import type { DraftId } from "../draftId";
import { usePlanApprovalControls } from "./chat/usePlanApprovalControls";
import { ChatLoadedThreadRuntime } from "./chat/ChatLoadedThreadRuntime";
import { buildChatLoadedThreadRuntimeInput } from "./chat/buildChatLoadedThreadRuntimeInput";
import { renderChatThreadBootstrapSurface } from "./chat/renderChatThreadBootstrapSurface";
import { useChatRouteRuntime } from "./chat/useChatRouteRuntime";
import { useChatThreadActionsRuntime } from "./chat/useChatThreadActionsRuntime";
import { useChatThreadControlsRuntime } from "./chat/useChatThreadControlsRuntime";
import { useChatThreadRuntimeState } from "./chat/useChatThreadRuntimeState";
import { useChatComposerUsageNotice } from "./chat/composer-notices/useChatComposerUsageNotice";
import { useChatComposerProviderConfiguration } from "./chat/useChatComposerProviderConfiguration";

export type ChatViewProps =
  | {
      environmentId: EnvironmentId;
      threadId: ThreadId;
      onDiffPanelOpen?: () => void;
      showRightPanelLauncher?: boolean;
      routeKind: "server";
      draftId?: never;
    }
  | {
      environmentId: EnvironmentId;
      threadId: ThreadId;
      onDiffPanelOpen?: () => void;
      showRightPanelLauncher?: boolean;
      routeKind: "draft";
      draftId: DraftId;
    };

export function ChatViewOrchestrator(props: ChatViewProps) {
  return (
    <div className="contents" data-chat-shortcut-surface={props.threadId}>
      <ChatViewActiveThreadRuntime {...props} />
    </div>
  );
}

function ChatViewActiveThreadRuntime(props: ChatViewProps) {
  const routeRuntime = useChatRouteRuntime(props);
  return <ChatViewBootstrappedThreadRuntime props={props} routeRuntime={routeRuntime} />;
}

interface ChatViewBootstrappedThreadRuntimeProps {
  readonly props: ChatViewProps;
  readonly routeRuntime: ReturnType<typeof useChatRouteRuntime>;
}

function pendingPlanPromptId(
  thread: ChatViewBootstrappedThreadRuntimeProps["routeRuntime"]["bootstrap"]["activeThread"],
): string | null {
  return thread?.pendingAutoCreatePrompt?.promptId ?? null;
}

/**
 * The submission-side model selection: what the next turn actually runs on.
 *
 * The Composer composes its own `useComposerModelSelection` for display, over
 * the unfiltered inventory it renders (see `providerAvailability` below) —
 * that is deliberate, not a discrepancy: the picker must keep showing a
 * Withheld Provider with its reason rather than dropping it. This call is the
 * one submission (`onSend`) reads, and it composes over the eligible subset
 * instead, so a person can never send a turn to a provider the picker only
 * showed them as unavailable. This read subscribes to the model picker's
 * slice only, never to the Draft's text, so typing does not re-render the
 * timeline and header around it.
 */
function ChatViewBootstrappedThreadRuntime(input: ChatViewBootstrappedThreadRuntimeProps) {
  const { props, routeRuntime } = input;
  const {
    environmentId,
    threadId,
    routeKind,
    onDiffPanelOpen,
    showRightPanelLauncher = true,
  } = props;
  const { bootstrap } = routeRuntime;
  const { activeThread, activeThreadId, composerActiveProvider, providerStatuses } = bootstrap;
  const { serverThreadStatus, serverThreadSubscriptionError } = bootstrap;
  const { isConnecting, isRevertingCheckpoint } = routeRuntime.runtimeFlags;

  const threadRuntime = useChatThreadRuntimeState({
    routeRuntime,
    threadId,
    isConnecting,
    isRevertingCheckpoint,
  });
  const controlsRuntime = useChatThreadControlsRuntime({
    routeRuntime,
    threadRuntime,
    onDiffPanelOpen,
  });
  const { activeProject } = threadRuntime.projectContext;
  const providerConfiguration = useChatComposerProviderConfiguration({
    environmentId,
    providerStatuses,
    composerActiveProvider: composerActiveProvider ?? null,
    composerDraftTarget: bootstrap.composerDraftTarget,
    activeThread,
    activeProject,
    settings: routeRuntime.settings,
  });
  const {
    selectedProvider,
    lockedProvider,
    composerDisabledReason,
    providerAccess,
    providerCredentialMode,
    providerAvailability,
    activeProviderStatus,
    composerModelSelection,
  } = providerConfiguration;
  const usageNotice = useChatComposerUsageNotice({
    threadId: String(activeThreadId),
    composerModelSelection,
    activeThread,
    latestTurnId: threadRuntime.projectContext.activeLatestTurn?.turnId ?? null,
    activity: threadRuntime.activity,
    pendingInput: threadRuntime.pendingInput,
  });
  const actions = useChatThreadActionsRuntime({
    routeRuntime,
    threadRuntime,
    controlsRuntime,
    routeThreadId: threadId,
    lockedProvider,
    composerModelSelection,
    composerDisabledReason,
    onOpenUsageLimits: usageNotice.open,
    onAcceptedTurnStart: usageNotice.clearForAcceptedTurn,
  });
  // M4.5 — plan approval handlers
  const planApproval = usePlanApprovalControls({
    environmentId,
    activeThreadId,
    promptId: pendingPlanPromptId(activeThread),
    planApprovalMode: routeRuntime.settings.agentOrchestration.planApproval,
    workerSpawnMode: routeRuntime.settings.agentOrchestration.workerSpawn,
  });

  const bootstrapSurface = renderChatThreadBootstrapSurface({
    activeThread,
    projectTitle: activeProject?.name ?? null,
    routeKind,
    serverThreadStatus,
    serverThreadSubscriptionError: serverThreadSubscriptionError ?? null,
  });

  return bootstrapSurface !== null ? (
    bootstrapSurface
  ) : (
    <ChatLoadedThreadRuntime
      {...buildChatLoadedThreadRuntimeInput({
        routeRuntime,
        threadRuntime,
        controlsRuntime,
        actions,
        planApproval,
        providerState: {
          selectedProvider,
          lockedProvider,
          composerDisabledReason,
          providerAccess,
          providerCredentialMode,
          providerAvailability,
        },
        activeProviderStatus,
        usageNotice: usageNotice.item,
        onOpenUsageLimits: usageNotice.open,
        options: { showRightPanelLauncher },
      })}
    />
  );
}
